import { CommonSubmittalUtils, Constants, FeedItemDoc, SubmittalDoc, SubmittalRevision, SubmittalSettingsDoc, WorkflowState } from 'common';
import { ConcourseDb } from '../../global/ConcourseDb';
import { authFetch } from '../../global/service/AuthService';

export class SubmittalService {
  //private static submittalDbInstances: { [projectId: string]: PouchDB.Database } = {};

  private static getBaseUrl(): string {
    return `${globalThis.ccLambdaBaseUrl}/fn/submittals`;
  }

  /**
   * Get the submittals database for real-time sync
   */
  static getSubmittalsDbName(projectId: string): string {
    return `p-${projectId}-submittals`;
  }
  static getSubmittalsDb(projectId: string): ConcourseDb {
    return ConcourseDb.getInstance(this.getSubmittalsDbName(projectId));
  }

  static filterSubmittalDocs(docs: any[]): SubmittalDoc[] {
    return (docs || []).filter(doc => doc && !Constants.isSettingsDoc(doc) && !doc.deleted_date && !String(doc._id || '').startsWith('_'));
  }

  static findSettingsDoc(docs: any[]): SubmittalSettingsDoc | undefined {
    return (docs || []).find(doc => Constants.isSettingsDoc(doc)) as SubmittalSettingsDoc | undefined;
  }

  static async getSettings(projectId: string): Promise<SubmittalSettingsDoc> {
    const db = this.getSubmittalsDb(projectId);
    const settingsDoc = await db.getDoc(Constants.SETTINGS_DOC_ID);
    return settingsDoc ?? CommonSubmittalUtils.getDefaultSubmittalSettingsDoc();
  }

  static async saveSettings(projectId: string, settings: SubmittalSettingsDoc): Promise<SubmittalSettingsDoc> {
    const db = this.getSubmittalsDb(projectId);
    settings._id = 'settings';
    return await db.saveDoc(settings);
  }

  static getFeedDb(projectId: string, submittalId: string): ConcourseDb {
    return ConcourseDb.getInstance(CommonSubmittalUtils.getFeedDbName(projectId, submittalId));
  }

  static getMarkupsDb(projectId: string, submittalId: string): ConcourseDb {
    return ConcourseDb.getInstance(CommonSubmittalUtils.getMarkupsDbName(projectId, submittalId));
  }

  static async storeFeedItem(projectId: string, submittalId: string, item: FeedItemDoc): Promise<FeedItemDoc> {
    const db = this.getFeedDb(projectId, submittalId);
    return await db.saveDoc(item);
  }


  /**
   * Get all submittals for a project using ConcourseDb (PouchDB with proxy sync)

  static async getSubmittals(projectId: string): Promise<SubmittalDoc[]> {
    try {
      // Use ConcourseDb for direct PouchDB sync with proxy
      const submittalsDb = ConcourseDb.getInstance(`p-${projectId}-submittals`);
      const submittals = await submittalsDb.getDocs();
      return submittals.filter((doc: SubmittalDoc) => !doc.deleted_date);
    } catch (error) {
      console.error('Error loading submittals from PouchDB:', error);
      return [];
    }
  }


*/
  /**
   * Get all submittals for a project (API only)
   */
  static async getSubmittalsApi(projectId: string): Promise<SubmittalDoc[]> {
    const response = await authFetch(`${this.getBaseUrl()}/list/${projectId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to get submittals: ${response.statusText}`);
    }

    const result = await response.json();
    return result.submittals || [];
  }

  /**
   * Get a specific submittal by ID using ConcourseDb (PouchDB with proxy sync)

  static async getSubmittal(projectId: string, submittalId: string): Promise<SubmittalDoc | null> {
    try {
      // Use ConcourseDb for direct PouchDB sync with proxy
      const submittalsDb = ConcourseDb.getInstance(`p-${projectId}-submittals`);
      const submittal = await submittalsDb.getDoc(submittalId);
      
      // Check if submittal is deleted
      if (submittal.deleted_date) {
        return null;
      }
      
      return submittal;
    } catch (error) {
      if (error.statusCode === 404) {
        return null;
      }
      console.error('Error loading submittal from PouchDB:', error);
      throw new Error(`Failed to get submittal: ${error.message}`);
    }
  }   */

  /**
   * Update an existing submittal using ConcourseDb (PouchDB with proxy sync)
   */
  static async updateSubmittal(projectId: string, submittal: SubmittalDoc): Promise<SubmittalDoc> {
    try {
      // Use ConcourseDb for direct PouchDB sync with proxy
      const submittalsDb = ConcourseDb.getInstance(`p-${projectId}-submittals`);

      // Add metadata for tracking
      submittal.updated_by = globalThis.user?.id;
      submittal.updated_date = new Date().toISOString();

      // Save to PouchDB (will sync to proxy)
      const savedSubmittal = await submittalsDb.saveDoc(submittal);
      return savedSubmittal;
    } catch (error) {
      console.error('Error updating submittal:', error);
      throw new Error(`Failed to update submittal: ${error.message}`);
    }
  }

  /**
   * Check if submittal number already exists
   */
  static async doesSubmittalNumberExist(projectId: string, number: string): Promise<boolean> {
    const submittals = this.filterSubmittalDocs(await this.getSubmittalsDb(projectId).getDocs());
    return submittals.some(submittal => submittal.code === number);
  }

  /**
   * First publish of a draft submittal. Posts the full SubmittalDoc so unsynced
   * draft edits are included. Maps to: POST /fn/submittals/create
   */
  static async create(projectId: string, submittal: SubmittalDoc): Promise<SubmittalDoc> {
    const response = await authFetch(`${this.getBaseUrl()}/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        projectId,
        submittal,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Failed to create submittal: ${errorData.message || response.statusText}`);
    }

    return (await response.json()) as SubmittalDoc;
  }

  /**
   * Complete the next workflow state (forward only). Server loads the submittal
   * from CouchDB; optional nextState merges dueBy onto the matching state.
   *
   * Maps to: POST /fn/submittals/completeState
   */
  static async completeState(
    projectId: string,
    submittalId: string,
    stateId: string,
    nextState?: WorkflowState,
    message?: string,
  ): Promise<SubmittalDoc> {
    const response = await authFetch(`${this.getBaseUrl()}/completeState`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        projectId,
        submittalId,
        stateId,
        nextState,
        message,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Failed to change submittal status: ${errorData.message || response.statusText}`);
    }

    return (await response.json()) as SubmittalDoc;
  }

  static async returnToState(projectId: string, submittalId: string, stateId: string, message?: string): Promise<SubmittalDoc> {
    const response = await authFetch(`${this.getBaseUrl()}/returnToState`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ projectId, submittalId, stateId, message }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Failed to return submittal to state: ${errorData.message || response.statusText}`);
    }

    const result = await response.json();
    return result as SubmittalDoc;
  }

  /**
   * Ensure the per-submittal feed CouchDB exists (creates + seeds from project feed if needed).
   */
  static async ensureFeedDb(projectId: string, submittalId: string): Promise<Response> {
    const response = await authFetch(`${this.getBaseUrl()}/ensureFeedDb`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ projectId, submittalId }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Failed to ensure submittal feed db: ${errorData.message || response.statusText}`);
    }
    return response;
  }

  /**
   * Ensure the per-submittal markups CouchDB exists.
   */
  static async ensureMarkupsDb(projectId: string, submittalId: string): Promise<void> {
    const response = await authFetch(`${this.getBaseUrl()}/ensureMarkupsDb`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ projectId, submittalId }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Failed to ensure submittal markups db: ${errorData.message || response.statusText}`);
    }
  }

  static async exportPdf(projectId: string, submittalId: string): Promise<void> {
    const response = await authFetch(`${this.getBaseUrl()}/export-pdf/${projectId}/${submittalId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const contentType = response.headers.get('Content-Type') || '';
      if (contentType.includes('application/json')) {
        const data = await response.json();
        throw new Error(data.message || data.error || response.statusText);
      }
      throw new Error(`Failed to export submittal PDF (${response.status})`);
    }

    const contentType = response.headers.get('Content-Type') || '';
    if (contentType.includes('application/json')) {
      const data = await response.json();
      if (!data?.signedUrl) {
        throw new Error(data?.message || data?.error || 'Failed to export submittal PDF');
      }
      await this.downloadFromSignedUrl(data.signedUrl, data.fileName || `submittal-${submittalId}.pdf`);
      return;
    }

    if (!contentType.includes('application/pdf')) {
      throw new Error('Unexpected response from export endpoint');
    }

    const blob = await response.blob();
    const contentDisposition = response.headers.get('Content-Disposition') || '';
    const fileNameMatch = contentDisposition.match(/filename="([^"]+)"/);
    const fileName = fileNameMatch?.[1] || `submittal-${submittalId}.pdf`;
    await this.downloadBlob(blob, fileName);
  }

  static async downloadBlob(blob: Blob, fileName: string): Promise<void> {
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  }

  static async downloadFromSignedUrl(signedUrl: string, fileName: string): Promise<void> {
    const response = await fetch(signedUrl);
    if (!response.ok) {
      throw new Error('Failed to download PDF');
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  }
}
